import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Brain, Code2, Palette, Server } from "lucide-react";

const capabilities = [
  { icon: Palette, title: "Design", desc: "UI • UX • Motion • 3D", desc2: "Figma • Framer • Spline • WebGL", color: "#FF2D9A", color2: "#00E5FF" },
  { icon: Code2, title: "Engineering", desc: "Frontend • Backend • APIs", desc2: "React • Node • Python • Microservices", color: "#7A5CFF", color2: "#FF2D9A" },
  { icon: Brain, title: "AI Systems", desc: "Models • Agents • Automation", desc2: "OpenAI • Claude • LangChain • Custom\u00A0AI", color: "#00E5FF", color2: "#7A5CFF" },
  { icon: Server, title: "Infrastructure", desc: "Cloud • Compute • DevOps", desc2: "AWS • GCP • GPUs • CI/CD", color: "#00E5FF", color2: "#FF2D9A" },
];

const easeOut = [0.22, 1, 0.36, 1];

function useCardTilt() {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50, active: false });

  const onMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({ rx: (y - 0.5) * -12, ry: (x - 0.5) * 12, mx: x * 100, my: y * 100, active: true });
  }, []);

  const onLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0, mx: 50, my: 50, active: false });
  }, []);

  return { tilt, onMove, onLeave };
}

export default function Capabilities() {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [scrollLit, setScrollLit] = useState(new Set());
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setScrollLit((prev) => {
          const next = new Set(prev);
          entries.forEach((e) => {
            const idx = Number(e.target.dataset.idx);
            if (e.isIntersecting) next.add(idx);
            else next.delete(idx);
          });
          return next;
        });
      },
      { rootMargin: "-30% 0px -30% 0px" }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isLit = useCallback(
    (i) => hoveredIdx === i || (hoveredIdx === null && scrollLit.has(i)),
    [hoveredIdx, scrollLit]
  );

  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="relative py-32 md:py-48"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[150px] section-glow pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2 }}
          className="sys-label mb-16 md:mb-24"
        >
          SYS.STACK // AI
        </motion.p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" style={{ perspective: "800px" }}>
          {capabilities.map((cap, i) => (
            <CapCard key={cap.title} cap={cap} i={i} cardRefs={cardRefs} setHoveredIdx={setHoveredIdx} isLit={isLit(i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CapCard({ cap, i, cardRefs, setHoveredIdx, isLit }) {
  const { tilt, onMove, onLeave } = useCardTilt();

  return (
    <motion.div
      ref={(el) => (cardRefs.current[i] = el)}
      data-idx={i}
      data-testid={`capability-card-${cap.title.toLowerCase().replace(/\s/g, "-")}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1, delay: i * 0.1, ease: easeOut }}
      onMouseEnter={() => setHoveredIdx(i)}
      onMouseMove={onMove}
      onMouseLeave={() => { setHoveredIdx(null); onLeave(); }}
      className="relative rounded-2xl overflow-hidden cursor-pointer"
      style={{
        padding: "1px",
        transform: tilt.active
          ? `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateY(-4px) scale(1.03)`
          : isLit
            ? "translateY(-3px)"
            : "translateY(0)",
        transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        transformStyle: "preserve-3d",
      }}
    >
      <div
        className="absolute inset-[-80%]"
        style={{
          background: `conic-gradient(from 0deg, transparent 30%, ${cap.color}90, ${cap.color2}90, transparent 70%)`,
          animation: `spin ${isLit || tilt.active ? 4 : 8}s linear infinite`,
        }}
      />

      <div
        className="absolute inset-0 rounded-2xl"
        style={{ background: `linear-gradient(135deg, ${cap.color}30, ${cap.color2}30)` }}
      />

      <div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{
          boxShadow: isLit || tilt.active
            ? `0 0 20px ${cap.color}15, 0 0 40px ${cap.color}08`
            : `0 0 10px ${cap.color}08`,
          transition: "box-shadow 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      <div className="relative bg-[#0A0E13] rounded-2xl p-5 md:p-6 z-10 text-center min-h-[180px] flex flex-col">
        {/* Cursor spotlight */}
        <div
          className="absolute inset-0 pointer-events-none rounded-2xl overflow-hidden"
          style={{
            background: tilt.active
              ? `radial-gradient(circle at ${tilt.mx}% ${tilt.my}%, ${cap.color}15, transparent 50%)`
              : "none",
            opacity: tilt.active ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}
        />

        <cap.icon
          size={20}
          className="mx-auto mb-5 relative"
          strokeWidth={1.2}
          style={{
            color: isLit || tilt.active ? cap.color : "rgba(161, 161, 170, 0.45)",
            filter: isLit || tilt.active ? `drop-shadow(0 0 6px ${cap.color}50)` : "none",
            transition: "all 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        <h3
          className="font-heading text-sm md:text-base font-light mb-2 relative pop-click"
          style={{
            color: isLit || tilt.active ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.7)",
            transition: "color 0.5s ease",
          }}
        >
          {cap.title}
        </h3>

        <p
          className="font-body text-[11px] leading-relaxed relative mx-auto break-words pop-click"
          style={{
            color: isLit || tilt.active ? "rgba(161,161,170,0.8)" : "rgba(113,113,122,0.7)",
            transition: "color 0.5s ease",
            maxWidth: "90%",
          }}
        >
          {cap.desc}
        </p>

        <p
          className="font-body text-[11px] leading-relaxed relative mt-2.5 mx-auto break-words pop-click"
          style={{
            color: isLit || tilt.active ? "rgba(161,161,170,0.65)" : "rgba(113,113,122,0.55)",
            transition: "color 0.5s ease",
            maxWidth: "90%",
          }}
        >
          {cap.desc2}
        </p>
      </div>
    </motion.div>
  );
}
